/**
 * Document List Page
 *
 * One list of all documents the user can access (owned + shared + organisation
 * -wide). The Visibility column distinguishes ownership only (yours vs shared
 * with you); the full audience — org-wide visibility, role/unit shares,
 * per-person grants — is shown and managed in the Share dialog.
 *
 * Data flow:
 *   - The list is read via React Query `useInfiniteQuery` (one page per "Load
 *     more"), so it inherits the app's cold-start resilience — `retry: 2` +
 *     the cold-start 503 retry in client.ts ride out a scale-to-zero backend
 *     (OpenFGA on the ACL path) warming up, showing the skeleton instead of a
 *     hard error during the warmup window.
 *   - Every mutation (delete, visibility, re-derive, bulk) is SERVER-
 *     AUTHORITATIVE: call the API, then `invalidateQueries` to refetch the
 *     committed state. NO optimistic local edits — for clinical documents,
 *     showing a row as deleted/re-scoped before the server confirms is the
 *     dangerous path. The row changes only when the server says so.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { ChevronLeft, FileText, Upload, Share2, RefreshCw, Globe, Lock, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { StatusBadge } from '@/components/documents/StatusBadge';
import { localisedTitle } from '@/lib/localisedTitle';
import {
  listDocuments,
  deleteDocument,
  searchDocuments,
  publishDocument,
  createDocumentVersion,
  RETRYABLE_STATUSES,
  type Document,
  type PublishScope,
  type DocumentSearchResult,
  type ExtractResult,
  type UnreadableSourceResult,
} from '@/api/endpoints/documents';
import {
  ApprovalRequestList,
  isDocumentAccessRequest,
  useApprovalsInbox,
} from '@/components/approvals/ApprovalsInbox';
import { SegmentedControl } from '@/components/ui/segmented-control';
import { createLogger } from '@/utils/logger';
import { sanitizeErrorMessage } from '@/utils/errorMessages';
import { BULK_DOCUMENT_CONCURRENCY, DOCUMENT_PAGE_SIZE } from '@/lib/constants';
import { DOCUMENTS_QUERY_KEY } from '@/lib/queryKeys';
import { formatDayStamp } from '@/utils/dates';
import { ApiError } from '@/api/client';
import ShareDialog from '@/components/ShareDialog';
import { useAuthStore } from '@/stores/auth';
import { toast } from '@/hooks/use-toast';
import { errorToast } from '@/hooks/errorToast';

const logger = createLogger('DocumentList');

// Page size for document listing — deploy-tunable via VITE_DOCUMENT_PAGE_SIZE.
const LIMIT = DOCUMENT_PAGE_SIZE;

/**
 * Run `fn` over `items` with at most `limit` concurrent in-flight calls.
 * Resolves with how many succeeded vs failed — never rejects, so a partial
 * failure (some rows 403/409, some OK) is reported as a summary rather than
 * losing the successes. `onItem` lets the caller reconcile per-row state from
 * each successful result as it lands.
 *
 * `limit` defaults to BULK_DOCUMENT_CONCURRENCY (the operator-tunable cap in
 * lib/constants) so a 50-row bulk action doesn't open 50 sockets against a
 * cold-starting backend.
 */
async function runBulk<T, R>(
  items: readonly T[],
  fn: (item: T) => Promise<R>,
  onItem: (item: T, result: R) => void,
  // labelFor maps an item to a short identifier (e.g. document id) for the
  // per-failure log line — avoids dumping the whole item object.
  labelFor: (item: T) => string,
  limit = BULK_DOCUMENT_CONCURRENCY
): Promise<{ done: number; failed: number }> {
  let done = 0;
  let failed = 0;
  let cursor = 0;
  const worker = async (): Promise<void> => {
    while (cursor < items.length) {
      const item = items[cursor++];
      try {
        const result = await fn(item);
        onItem(item, result);
        done += 1;
      } catch (err) {
        // The summary toast only reports a failed count — log each failure so a
        // partial bulk (e.g. 5 of 50 fail) leaves a trace of WHICH item and WHY
        // (transient network vs persistent ACL/permission), for diagnosis.
        const code = err instanceof ApiError ? err.code : undefined;
        const status = err instanceof ApiError ? err.status : undefined;
        const message = err instanceof Error ? err.message : 'unknown error';
        logger.warn('Bulk item failed', { item: labelFor(item), code, status, error: message });
        failed += 1;
      }
    }
  };
  await Promise.all(
    Array.from({ length: Math.min(limit, items.length) }, () => worker())
  );
  return { done, failed };
}

/**
 * Localise an extract's `kind` (config data, e.g. "dose") with a
 * capitalized fallback for kinds without a translation — mirrors
 * formatCategory's fallback-to-raw-value rule below ("kind is data",
 * 2026-07-30 structured-extracts generalisation).
 */
function formatExtractKind(kind: string, t: TFunction): string {
  if (!kind) return t('documents.list.extractKind.unknown', 'Unknown');
  const key = `documents.list.extractKind.${kind}`;
  const translated = t(key);
  return translated === key ? kind.charAt(0).toUpperCase() + kind.slice(1) : translated;
}

/**
 * One structured extract rendered inside the content-search results
 * (2026-07-30 structured-extracts generalisation). `text` renders verbatim,
 * in emphasis, exactly as the API returned it — never reformatted, never
 * translated. `term` is the pre-resolved locale-fallback line from
 * `extract.terms` (`localisedTitle` in the parent). `kind` labels what
 * kind of fact this is (e.g. "dose") so a dosing statement still reads as
 * one now that the surrounding UI chrome is generic.
 */
function ExtractEntry({
  extract,
  term,
  t,
}: {
  extract: ExtractResult;
  term: string | undefined;
  t: TFunction;
}) {
  return (
    <div className="rounded-md bg-primary/5 border border-primary/20 px-2.5 py-1.5">
      {term && <p className="text-xs text-neutral-600">{term}</p>}
      <p className="text-xs font-semibold italic text-neutral-900 mt-0.5">{extract.text}</p>
      <div className="flex items-center gap-2 mt-1 text-[11px] text-neutral-500">
        <span className="font-medium uppercase tracking-wide">{formatExtractKind(extract.kind, t)}</span>
        {extract.page_start > 0 && <span>{t('documents.list.extractPage', 'p. {{page}}', { page: extract.page_start })}</span>}
        {extract.source_kind === 'figure' && <span>{t('documents.list.extractFromFigure', 'from figure')}</span>}
      </div>
    </div>
  );
}

export default function DocumentList() {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const user = useAuthStore((s) => s.user);

  const queryClient = useQueryClient();

  // The document list is read through React Query (like every other data screen)
  // so it inherits the app's cold-start resilience: `retry: 2` with backoff in
  // App.tsx + the cold-start 503 retry in client.ts ride out a scale-to-zero
  // backend warming up (OpenFGA ListObjects on the ACL path) instead of
  // hard-erroring on the first 503. While retries are in flight the query stays
  // `pending` (the skeleton shows) — the nurse sees "loading", never a red error
  // mid-warmup. useInfiniteQuery models the "Load more" pagination natively.
  const documentsQuery = useInfiniteQuery({
    queryKey: DOCUMENTS_QUERY_KEY,
    queryFn: ({ pageParam }) => listDocuments(LIMIT, pageParam),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // An empty page is the end — without this guard a stale-high totalCount
      // (e.g. concurrent deletes) would keep hasNextPage true and "Load more"
      // would refetch the same empty offset forever.
      if (lastPage.documents.length === 0) return undefined;
      const loaded = allPages.reduce((n, p) => n + p.documents.length, 0);
      return loaded < (lastPage.totalCount ?? 0) ? loaded : undefined;
    },
  });

  // Flattened view of every loaded page + the authoritative total.
  const documents: Document[] = documentsQuery.data?.pages.flatMap((p) => p.documents) ?? [];
  const loadedPages = documentsQuery.data?.pages ?? [];
  const total = loadedPages.length > 0 ? (loadedPages[loadedPages.length - 1].totalCount ?? 0) : 0;

  // Server-authoritative refresh after a mutation: refetch the loaded pages so
  // the list reflects committed state. NO optimistic local edits — for clinical
  // documents, showing a row as deleted/re-scoped before the server confirms is
  // the dangerous path (Joël). The row changes only when the server says so.
  const refreshDocuments = useCallback(
    () => queryClient.invalidateQueries({ queryKey: DOCUMENTS_QUERY_KEY }),
    [queryClient]
  );

  // The DOCUMENT slice of the approvals inbox — publish requests plus
  // document access requests. Unit requests live on /approvals (and each
  // unit's own page); they have no business on the Knowledge Base.
  const inbox = useApprovalsInbox();
  const docRequests = inbox.requests.filter(isDocumentAccessRequest);
  const docApprovalsCount = docRequests.length;

  // Which tab is showing: the document list or the document approvals queue.
  const [tab, setTab] = useState<'documents' | 'approvals'>('documents');

  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  // Sharing state
  const [sharingDocId, setSharingDocId] = useState<string | null>(null);
  const [sharingDocTitle, setSharingDocTitle] = useState('');

  // Per-row re-derivation in flight (disables that row's Retry button).
  const [rederivingId, setRederivingId] = useState<string | null>(null);

  // Bulk selection (manageable docs only). A bulk op disables the whole bar
  // while in flight; the destructive bulk-delete gets an inline confirm step.
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [confirmBulkDelete, setConfirmBulkDelete] = useState(false);

  // Search query (debounced → hits backend content search when non-empty)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<DocumentSearchResult[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  // Extracts/unreadable sources alongside the search results (2026-07-30
  // structured-extracts generalisation) — [] never null from the API, matched here.
  const [searchExtracts, setSearchExtracts] = useState<ExtractResult[]>([]);
  const [searchUnreadableSources, setSearchUnreadableSources] = useState<UnreadableSourceResult[]>([]);

  // Helper: format category with i18n. Input is `unknown` to guard against
  // non-string runtime values from the search-result wire shape (which is
  // not the same converter as the admin pending-docs endpoint).
  const formatCategory = (category: unknown): string => {
    const s = typeof category === 'string' ? category : '';
    if (!s) return t('documents.list.category.unknown');
    const key = `documents.list.category.${s}`;
    const translated = t(key);
    // Fallback to capitalized category if translation key doesn't exist
    return translated === key ? s.charAt(0).toUpperCase() + s.slice(1) : translated;
  };

  // A user can manage (change visibility / delete / retry) a document when the
  // server reports them as its owner via the ACL effective_role, OR when they
  // are the owner_id. This mirrors the backend authorization exactly: delete
  // and visibility are owner-only (CanDelete / CanShare). Both signals are
  // needed: effective_role catches docs surfaced via an ACL owner grant whose
  // owner_id differs (e.g. admin-seeded uploads), while the owner_id check
  // catches the user's own docs that the list includes via OwnerId but whose
  // ACL grant wasn't in the ListAccessible set (so effective_role is absent —
  // it's `omitempty` on the wire). The list itself 503s when ACL is down
  // (no degraded path since #1482), so this is never a fallback for an
  // ACL-unavailable list — only for a row the grant query didn't cover.
  const canManage = useCallback(
    (doc: Document): boolean =>
      doc.effective_role === 'owner' || doc.owner_id === user?.id,
    [user?.id]
  );

  // A user can RETRY (re-derive) a document when they hold write access —
  // owner OR editor — because POST /documents/{id}/versions is gated
  // server-side by the docEditor middleware, NOT owner-only like
  // delete/visibility. Gating
  // the per-row Retry on canManage (owner-only) would hide it from an editor
  // who has write access, defeating the editor role for recovery. owner_id is
  // the same omitempty-fallback as canManage (own doc not in the ACL grant set).
  const canRetry = useCallback(
    (doc: Document): boolean =>
      doc.effective_role === 'owner' ||
      doc.effective_role === 'editor' ||
      doc.owner_id === user?.id,
    [user?.id]
  );

  // Retryable = a failed ingestion that can be re-run. `quarantined` is a
  // safety hold, not a transient failure, so it is intentionally excluded.
  const isRetryable = (doc: Document): boolean => RETRYABLE_STATUSES.has(doc.status);

  // Debounced content search. Empty query clears results (return to list mode).
  useEffect(() => {
    const q = searchQuery.trim();
    if (!q) {
      setSearchResults(null);
      setSearchExtracts([]);
      setSearchUnreadableSources([]);
      setSearchError(null);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    setSearchError(null);
    const handle = setTimeout(() => {
      searchDocuments(q)
        .then((resp) => {
          setSearchResults(resp.results);
          setSearchExtracts(resp.extracts);
          setSearchUnreadableSources(resp.unreadable_sources);
        })
        .catch((err: unknown) => {
          const msg = err instanceof Error ? err.message : 'Search failed';
          logger.error('Search failed', { q, error: msg });
          setSearchError(sanitizeErrorMessage(msg));
          setSearchResults([]);
          setSearchExtracts([]);
          setSearchUnreadableSources([]);
        })
        .finally(() => setIsSearching(false));
    }, 300);
    return () => clearTimeout(handle);
  }, [searchQuery]);

  // Group extracts by their document so each result row can attach its own
  // extracts; an extract whose document did NOT otherwise rank in `results`
  // (the lexical lookup leg can surface a document the other legs missed)
  // renders in a compact block above the results list instead.
  const extractsByDocument = useMemo(() => {
    const map = new Map<string, ExtractResult[]>();
    for (const extract of searchExtracts) {
      const existing = map.get(extract.document_id);
      if (existing) existing.push(extract);
      else map.set(extract.document_id, [extract]);
    }
    return map;
  }, [searchExtracts]);

  const orphanExtracts = useMemo(() => {
    const resultDocIds = new Set((searchResults ?? []).map((r) => r.document_id));
    return searchExtracts.filter((e) => !resultDocIds.has(e.document_id));
  }, [searchExtracts, searchResults]);

  const handleDelete = async (documentId: string) => {
    setDeletingId(documentId);
    // A per-row action dismisses any live bulk-delete confirm so a stray click
    // on the lingering "Delete N documents?" prompt can't trigger a bulk delete
    // the nurse didn't mean (the old fetchDocuments reset this on every reload).
    setConfirmBulkDelete(false);

    try {
      await deleteDocument(documentId);
      logger.info('Document deleted', { documentId });
      setConfirmDeleteId(null);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(documentId);
        return next;
      });
      // Server-authoritative refresh — the row vanishes only after the server
      // confirms the delete (no optimistic removal).
      await refreshDocuments();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete document';
      logger.error('Delete failed', { documentId, error: msg });
      // A delete failure is a side error — surface it as a toast, never replace
      // the whole list with an error state (frontend resilience: keep the DATA).
      errorToast({
        context: 'deleteDocument',
        description: sanitizeErrorMessage(msg),
        onRetry: () => void handleDelete(documentId),
      });
    } finally {
      setDeletingId(null);
    }
  };

  // Retry a single failed document, then refetch so the row reflects the status
  // the server set. NO optimistic "processing" flip — the row updates when the
  // server confirms the re-derivation actually started. A 409 INVALID_STATE means
  // the document already left the failed state (someone else retried, or it
  // recovered) — we tell the user rather than offering an endless retry.
  const handleRederive = async (doc: Document) => {
    // Block only the already-in-flight row (so retrying a DIFFERENT failed doc
    // while one is running still works — a guard on ANY non-null rederivingId
    // would silently drop the second click, which the per-row `disabled` prop
    // does not signal). Also bail during a bulk run so a per-row Retry can't
    // race the bulk call on the same document.
    if (rederivingId === doc.id || bulkBusy) return;
    setRederivingId(doc.id);
    setConfirmBulkDelete(false); // a per-row action dismisses a live bulk-delete confirm
    try {
      await createDocumentVersion(doc.id);
      await refreshDocuments();
      toast({ description: t('documents.list.retry.success', 'Retry started.') });
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Retry failed';
      logger.error('Re-derivation failed', { documentId: doc.id, error: msg });
      const invalidState = err instanceof ApiError && err.code === 'INVALID_STATE';
      errorToast({
        context: 'createDocumentVersion',
        description: invalidState
          ? t('documents.list.retry.invalidState', 'This document is no longer in a state that can be retried.')
          : t('documents.list.retry.error', 'Could not retry processing. Please try again.'),
        // INVALID_STATE is terminal (the doc left the retryable state) — offering
        // Retry would loop on a guaranteed failure; fall back to Report.
        onRetry: invalidState ? undefined : () => void handleRederive(doc),
      });
    } finally {
      setRederivingId(null);
    }
  };

  // ---- Bulk selection ----
  // Only manageable docs are ever selectable, so every bulk op acts on rows the
  // server will accept (no 403s from selecting someone else's read-only doc).
  const manageableDocs = documents.filter(canManage);
  const allManageableSelected =
    manageableDocs.length > 0 && manageableDocs.every((d) => selectedIds.has(d.id));
  const selectedDocs = documents.filter((d) => selectedIds.has(d.id));
  // Mirror handleBulkReprocess's target predicate exactly (canRetry + isRetryable)
  // so the bulk Retry button never enables for a doc the action would skip.
  const anySelectedRetryable = selectedDocs.some((d) => canRetry(d) && isRetryable(d));

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    setSelectedIds((prev) =>
      // Membership check (not a size comparison): after a "Load more" append,
      // newly-surfaced manageable docs join `manageableDocs` without being in
      // the selection, so a `.size === length` test would desync and flip the
      // header checkbox the wrong way. Mirror `allManageableSelected` exactly.
      manageableDocs.length > 0 && manageableDocs.every((d) => prev.has(d.id))
        ? new Set()
        : new Set(manageableDocs.map((d) => d.id))
    );
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
    setConfirmBulkDelete(false);
  };

  // ---- Bulk operations ----
  // Each runs the per-document endpoint with bounded concurrency, reconciles
  // each row from its own response, and surfaces ONE summary toast. Partial
  // failures keep the successes (frontend resilience rule: never drop the data
  // state on a side error).
  const handleBulkPublish = async (scope: PublishScope) => {
    // Target every manageable doc whose org-wide state differs from the request
    // — including 'institution'/'department'-scoped docs, which the old
    // scope-equality filter silently skipped (the "Make organisation-wide
    // does nothing" bug). Org-wide is the group:public grant; the backend
    // toggles only that, so role shares are preserved.
    const wantOrgWide = scope === 'public';
    const targets = selectedDocs.filter((d) => canManage(d) && (d.visibility?.org_wide ?? false) !== wantOrgWide);
    if (targets.length === 0) {
      clearSelection();
      return;
    }
    setBulkBusy(true);
    try {
      // Rows that came back 202 APPROVAL_PENDING were NOT applied — they became
      // publish requests. Count them separately so the summary never claims an
      // expansion landed that is still awaiting an approver. Approver display
      // names are deduplicated across ALL pending results — different documents
      // (e.g. different categories) can name different reviewer sets, so
      // keeping only the last response's names would drop reviewers from the
      // toast.
      let pendingCount = 0;
      const approverNames = new Set<string>();
      const { done, failed } = await runBulk(
        targets,
        (d) => publishDocument(d.id, scope),
        (_d, result) => {
          if (result.status === 'pending') {
            pendingCount += 1;
            result.approvers.forEach((a) => {
              if (a.display_name) approverNames.add(a.display_name);
            });
          }
        },
        (d) => d.id
      );
      await refreshDocuments();
      const applied = done - pendingCount;
      if (failed > 0) {
        // Partial failure — some rows applied, some didn't. No Retry: re-running
        // the bulk would re-derive the rows that already succeeded; Report lets
        // the team see the partial failure.
        errorToast({
          context: 'bulkVisibility',
          description: t('documents.list.bulk.visibilityPartial', { done: applied, failed, defaultValue: '{{done}} updated, {{failed}} failed.' }),
        });
      } else if (applied > 0) {
        toast({
          description: t('documents.list.bulk.visibilityDone', { count: applied, defaultValue: '{{count}} document(s) updated.' }),
        });
      }
      if (pendingCount > 0) {
        toast({
          description:
            approverNames.size > 0
              ? t('documents.list.visibility.sentForApproval', {
                  names: [...approverNames].join(', '),
                  defaultValue: 'Sent for approval to {{names}}',
                })
              : t('documents.list.bulk.sentForApproval', {
                  count: pendingCount,
                  defaultValue: '{{count}} sent for approval.',
                }),
        });
      }
    } catch (err) {
      logger.error('Bulk visibility change failed', { error: sanitizeErrorMessage(err instanceof Error ? err.message : String(err)) });
      // No Retry: `clearSelection()` in finally empties the selection, so a
      // re-fire would act on nothing. Report surfaces the failure instead.
      errorToast({
        context: 'bulkVisibility',
        description: t('documents.list.bulk.error', 'Could not complete the bulk action. Please try again.'),
      });
    } finally {
      setBulkBusy(false);
      clearSelection();
    }
  };

  const handleBulkReprocess = async () => {
    // canRetry (owner|editor), matching the per-row Retry predicate — so the
    // bulk path can never silently skip an editor's retryable doc if selection
    // eligibility is ever widened.
    const targets = selectedDocs.filter((d) => canRetry(d) && isRetryable(d));
    if (targets.length === 0) {
      toast({ description: t('documents.list.bulk.noRetryable', 'None of the selected documents can be retried.') });
      clearSelection();
      return;
    }
    setBulkBusy(true);
    try {
      const { done, failed } = await runBulk(
        targets,
        (d) => createDocumentVersion(d.id),
        () => {},
        (d) => d.id
      );
      // Server-authoritative refresh — rows reflect the status the server set,
      // no optimistic "processing" flip or per-row revert to manage.
      await refreshDocuments();
      if (failed > 0) {
        // Partial failure — Report (no Retry: re-running would re-derive the
        // rows that already started).
        errorToast({
          context: 'bulkReprocess',
          description: t('documents.list.bulk.retryPartial', { done, failed, defaultValue: 'Retry started for {{done}}, {{failed}} failed.' }),
        });
      } else {
        toast({
          description: t('documents.list.bulk.retryDone', { count: done, defaultValue: 'Retry started for {{count}} document(s).' }),
        });
      }
    } catch (err) {
      logger.error('Bulk re-derivation failed', { error: sanitizeErrorMessage(err instanceof Error ? err.message : String(err)) });
      // No Retry: `clearSelection()` in finally empties the selection.
      errorToast({
        context: 'bulkReprocess',
        description: t('documents.list.bulk.error', 'Could not complete the bulk action. Please try again.'),
      });
    } finally {
      setBulkBusy(false);
      clearSelection();
    }
  };

  const handleBulkDelete = async () => {
    // Processing docs can't be deleted (same per-row rule); skip them.
    const targets = selectedDocs.filter((d) => canManage(d) && d.status !== 'processing');
    if (targets.length === 0) {
      clearSelection();
      return;
    }
    setBulkBusy(true);
    try {
      const { done, failed } = await runBulk(
        targets,
        (d) => deleteDocument(d.id),
        () => {},
        (d) => d.id
      );
      // Server-authoritative reconcile of rows/totals/pagination after deletes,
      // in the try (like the other bulk handlers) so a throw can't fire a
      // refresh + toast while the error is swallowed by finally.
      await refreshDocuments();
      if (failed > 0) {
        // Partial failure — Report (no Retry: re-running would re-delete the
        // rows that already succeeded, which 404 individually).
        errorToast({
          context: 'bulkDelete',
          description: t('documents.list.bulk.deletePartial', { done, failed, defaultValue: '{{done}} deleted, {{failed}} failed.' }),
        });
      } else {
        toast({
          description: t('documents.list.bulk.deleteDone', { count: done, defaultValue: '{{count}} document(s) deleted.' }),
        });
      }
    } catch (err) {
      logger.error('Bulk delete failed', { error: sanitizeErrorMessage(err instanceof Error ? err.message : String(err)) });
      // No Retry: `clearSelection()` in finally empties the selection.
      errorToast({
        context: 'bulkDelete',
        description: t('documents.list.bulk.error', 'Could not complete the bulk action. Please try again.'),
      });
    } finally {
      setBulkBusy(false);
      clearSelection();
    }
  };

  // Visibility column: ownership at a glance — yours, or shared with you.
  // The full audience (org-wide visibility, role/unit shares, per-person
  // grants, pending approvals) is shown and managed in the Share dialog.
  // Rendered only once the auth user is known: before Zustand rehydrates,
  // `user` is null and every row — including the caller's own — would claim
  // "Shared with you", the exact false statement this column exists to avoid.
  const renderVisibility = (doc: Document) =>
    user ? (
      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-neutral-100 text-neutral-600">
        {doc.owner_id === user.id
          ? t('documents.sharing.role.owner', 'Owner')
          : t('documents.list.visibility.sharedWithYou', 'Shared with you')}
      </span>
    ) : null;

  // List-load error (from the query). Search has its own searchError surface.
  const listErrorMsg = documentsQuery.isError
    ? sanitizeErrorMessage(
        documentsQuery.error instanceof Error ? documentsQuery.error.message : 'Failed to load documents'
      )
    : null;

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Header */}
      <header className="sticky top-0 z-10 bg-white border-b border-neutral-200">
        <div className="px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/home')}
              aria-label={t('documents.list.backHome')}
              className="w-9 h-9 -ml-2 flex items-center justify-center text-primary"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <h1 className="text-base font-semibold text-neutral-900">{t('documents.list.title')}</h1>
          </div>
          <Button onClick={() => navigate('/documents/upload')} size="sm">
            <Upload className="w-4 h-4 mr-1" />
            {t('documents.list.upload')}
          </Button>
        </div>
      </header>

      {/* Tabs: the document list vs the document approvals queue. The tab
          label carries the pending count — the in-page notification that
          something is waiting without occupying the list itself. */}
      <div className="px-4 pt-4 max-w-6xl mx-auto">
        <SegmentedControl
          value={tab}
          onChange={(v) => {
            setTab(v === 'approvals' ? 'approvals' : 'documents');
            // Disarm any bulk selection (and its delete confirm) on tab
            // switch — returning to a still-armed bulk bar is a misclick trap.
            clearSelection();
          }}
          options={[
            { id: 'documents', label: t('documents.tabs.documents', 'Documents') },
            {
              id: 'approvals',
              label:
                docApprovalsCount > 0
                  ? `${t('documents.tabs.approvals', 'Approvals')} (${docApprovalsCount})`
                  : t('documents.tabs.approvals', 'Approvals'),
            },
          ]}
          className="w-full"
        />
      </div>

      {tab === 'approvals' ? (
        /* Approvals tab — DOCUMENT requests only (publish + document access).
           All four states are rendered: error with retry, loading skeleton,
           empty, and the decision list. */
        <main className="p-4 max-w-6xl mx-auto">
          {inbox.isError ? (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center justify-between gap-3">
              <span>{t('documents.approvals.loadError', 'Could not load pending approvals.')}</span>
              <Button
                size="sm"
                variant="outline"
                onClick={() => inbox.refetch()}
                disabled={inbox.isFetching}
                className="shrink-0"
              >
                {inbox.isFetching ? t('common.loading') : t('actions.retry', 'Retry')}
              </Button>
            </div>
          ) : inbox.isPending ? (
            <div className="space-y-4">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : docApprovalsCount === 0 ? (
            <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
              <FileText className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
              <p className="text-neutral-600">{t('approvals.empty', 'No pending approvals.')}</p>
            </div>
          ) : (
            <section className="bg-white border border-neutral-200 rounded-xl overflow-hidden">
              {inbox.hasMore && (
                <p className="px-4 py-3 text-sm text-amber-700 bg-amber-50 border-b border-amber-200">
                  {t('documents.approvals.hasMore', 'More pending than shown — decide these to load the rest.')}
                </p>
              )}
              <ApprovalRequestList requests={docRequests} />
            </section>
          )}
        </main>
      ) : (
        <>
      {/* Search */}
      <div className="px-4 pt-2 pb-2 max-w-6xl mx-auto">
        <input
          type="search"
          inputMode="search"
          // Same autofill suppression as apps/compatibility/DrugSearch.tsx
          // (PR #1004) — tricorder.icu has saved logins, so Safari/iCloud
          // Keychain offers username autofill on any naked search input.
          name="document-filter"
          autoComplete="off"
          autoCorrect="off"
          autoCapitalize="off"
          spellCheck={false}
          data-1p-ignore="true"
          data-lpignore="true"
          data-bitwarden-ignore="true"
          data-form-type="other"
          placeholder={t('documents.list.search', 'Search documents...')}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="w-full px-3 py-2 border border-neutral-200 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-primary/20"
        />
      </div>

      {/* Content */}
      <main className="p-4 max-w-6xl mx-auto">
        {searchResults !== null ? (
          <div className="space-y-2">
            {isSearching && (
              <div className="text-xs text-neutral-500">{t('documents.list.searching', 'Searching…')}</div>
            )}
            {searchError ? (
              <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
                {searchError}
              </div>
            ) : (
              <>
                {/* Unreadable sources — explicit, never silent: the named document's
                    pages could not be read, so the fact must be looked up in the
                    source directly. */}
                {searchUnreadableSources.map((source) => (
                  <div
                    key={`unreadable-${source.document_id}`}
                    className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800"
                  >
                    {t(
                      'documents.list.unreadableSource',
                      '{{title}}: page(s) {{pages}} could not be read — check the source directly.',
                      { title: source.document_title || 'Untitled', pages: source.pages.join(', ') }
                    )}
                  </div>
                ))}
                {/* Extracts whose document did not otherwise rank in `results` —
                    the lexical lookup leg can surface a document the other legs missed. */}
                {orphanExtracts.length > 0 && (
                  <div className="bg-white border border-neutral-200 rounded-lg p-3 space-y-2">
                    <p className="text-xs font-medium text-neutral-500">
                      {t('documents.list.extractsHeading', 'Key statements')}
                    </p>
                    {orphanExtracts.map((extract) => (
                      <ExtractEntry
                        key={`orphan-${extract.document_id}-${extract.page_start}-${extract.subject}-${extract.text}`}
                        extract={extract}
                        term={localisedTitle(extract.terms, i18n.language)}
                        t={t}
                      />
                    ))}
                  </div>
                )}
                {searchResults.length === 0 && orphanExtracts.length === 0 && searchUnreadableSources.length === 0 && !isSearching ? (
                  <div className="bg-white border border-neutral-200 rounded-xl p-6 text-center text-sm text-neutral-600">
                    {t('documents.list.searchEmpty', 'No matches. Documents still processing are not searchable yet.')}
                  </div>
                ) : (
                  searchResults.map((r) => {
                    const extracts = extractsByDocument.get(r.document_id);
                    return (
                      <button
                        key={r.document_id}
                        onClick={() => navigate(`/documents/${r.document_id}`)}
                        className="w-full text-left bg-white border border-neutral-200 rounded-lg p-3 hover:bg-neutral-50"
                      >
                        <p className="font-medium text-sm">{r.title || 'Untitled'}</p>
                        {r.category && (
                          <p className="text-xs text-neutral-500 mt-0.5">{formatCategory(r.category)}</p>
                        )}
                        {r.snippet && (
                          <p className="text-xs text-neutral-700 mt-2 line-clamp-3">{r.snippet}</p>
                        )}
                        {extracts && extracts.length > 0 && (
                          <div className="mt-2 space-y-2 border-t border-neutral-100 pt-2">
                            {extracts.map((extract) => (
                              <ExtractEntry
                                key={`${extract.document_id}-${extract.page_start}-${extract.subject}-${extract.text}`}
                                extract={extract}
                                term={localisedTitle(extract.terms, i18n.language)}
                                t={t}
                              />
                            ))}
                          </div>
                        )}
                      </button>
                    );
                  })
                )}
              </>
            )}
          </div>
        ) : listErrorMsg ? (
          // ERROR state — only reached after React Query exhausts its retries
          // (retry: 2 + the cold-start 503 retry in client.ts). During the
          // cold-start window the query stays `pending`, so the skeleton shows
          // and the nurse never sees this mid-warmup. Retry is user-initiated.
          <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700 flex items-center justify-between gap-3">
            <span>{listErrorMsg}</span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void documentsQuery.refetch()}
              disabled={documentsQuery.isFetching}
              className="shrink-0"
            >
              {documentsQuery.isFetching ? t('common.loading') : t('actions.retry', 'Retry')}
            </Button>
          </div>
        ) : documentsQuery.isPending ? (
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        ) : documents.length === 0 ? (
          <div className="bg-white border border-neutral-200 rounded-xl p-8 text-center">
            <FileText className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
            <p className="text-neutral-600">{t('documents.list.empty.title')}</p>
          </div>
        ) : (
          <>
            {/* Bulk action bar — appears once any manageable doc is selected.
                Sticks under the header so the actions stay reachable while
                scrolling a long list. Acts only on selected manageable rows. */}
            {selectedIds.size > 0 && (
              <div className="sticky top-14 z-10 mb-3 flex flex-wrap items-center gap-2 rounded-lg border border-primary/20 bg-primary/5 px-3 py-2">
                <span className="text-sm font-medium text-neutral-700">
                  {t('documents.list.bulk.selected', { count: selectedDocs.length, defaultValue: '{{count}} selected' })}
                </span>
                <div className="flex-1" />
                {confirmBulkDelete ? (
                  <>
                    <span className="text-xs text-neutral-600">
                      {t('documents.list.bulk.confirmDelete', { count: selectedDocs.length, defaultValue: 'Delete {{count}} document(s)? This cannot be undone.' })}
                    </span>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleBulkDelete}
                      disabled={bulkBusy}
                      className="text-red-600 border-red-200 hover:bg-red-50 min-h-[44px]"
                    >
                      {bulkBusy ? t('documents.list.bulk.working', 'Working…') : t('documents.list.delete.confirmButton')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmBulkDelete(false)}
                      disabled={bulkBusy}
                      className="text-neutral-600 border-neutral-200 hover:bg-neutral-50 min-h-[44px]"
                    >
                      {t('documents.list.delete.cancel')}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBulkPublish('public')}
                      disabled={bulkBusy}
                      className="min-h-[44px]"
                    >
                      <Globe className="w-4 h-4 mr-1" />
                      {t('documents.list.bulk.makeOrganisation', 'Make organisation-wide')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleBulkPublish('private')}
                      disabled={bulkBusy}
                      className="min-h-[44px]"
                    >
                      <Lock className="w-4 h-4 mr-1" />
                      {t('documents.list.bulk.makePrivate', 'Make private')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={handleBulkReprocess}
                      disabled={bulkBusy || !anySelectedRetryable}
                      title={!anySelectedRetryable ? t('documents.list.bulk.noRetryable', 'None of the selected documents can be retried.') : undefined}
                      className="min-h-[44px]"
                    >
                      <RefreshCw className="w-4 h-4 mr-1" />
                      {t('documents.list.bulk.retry', 'Retry')}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setConfirmBulkDelete(true)}
                      disabled={bulkBusy}
                      className="text-red-600 border-red-200 hover:bg-red-50 min-h-[44px]"
                    >
                      {t('documents.list.bulk.delete', 'Delete')}
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={clearSelection}
                      disabled={bulkBusy}
                      className="min-h-[44px]"
                      aria-label={t('documents.list.bulk.clear', 'Clear selection')}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </>
                )}
              </div>
            )}

            {/* Mobile: Card Layout */}
            <div className="md:hidden space-y-2">
              {documents.map((doc) => {
                const isProcessing = doc.status === 'processing';
                const cannotDelete = isProcessing;
                const manageable = canManage(doc);
                const retryable = canRetry(doc) && isRetryable(doc);

                return (
                  <div
                    key={doc.id}
                    onClick={() => navigate(`/documents/${doc.id}`)}
                    className="bg-white border border-neutral-200 rounded-lg p-3 cursor-pointer hover:bg-neutral-50"
                  >
                    <div className="flex items-start justify-between gap-2">
                      {manageable && (
                        <label
                          className="flex items-center pt-0.5 pr-1 min-h-[44px] min-w-[44px] cursor-pointer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-primary"
                            checked={selectedIds.has(doc.id)}
                            onChange={() => toggleSelect(doc.id)}
                            aria-label={t('documents.list.bulk.selectRow', { title: doc.title || 'Untitled', defaultValue: 'Select {{title}}' })}
                          />
                        </label>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-sm truncate">{doc.title || 'Untitled'}</p>
                        {doc.owner_id !== user?.id && doc.owner_name && (
                          <p className="text-xs text-neutral-500 truncate">
                            {t('documents.list.byOwner', { name: doc.owner_name, defaultValue: 'by {{name}}' })}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-1">
                          <StatusBadge
                            status={doc.status}
                            errorMessage={doc.error_message}
                            canAct={canRetry(doc)}
                          />
                          <span className="text-xs text-neutral-500">{formatCategory(doc.category)}</span>
                        </div>
                        <div className="flex items-center gap-2 mt-2" onClick={(e) => e.stopPropagation()}>
                          <span className="text-xs text-neutral-400">{t('documents.list.table.visibility', 'Visibility')}:</span>
                          {renderVisibility(doc)}
                        </div>
                        <p className="text-xs text-neutral-400 mt-1">{formatDayStamp(doc.created_at, t)}</p>
                      </div>
                      {/* Actions row. Share + Delete are owner-only (manageable);
                          Retry is shown to anyone with write access (retryable,
                          which includes editors) — so the row renders when the
                          user can manage OR retry. Stop bubbling so buttons don't
                          trigger row navigation. */}
                      {(manageable || retryable) && (
                        <div className="flex items-start gap-2" onClick={(e) => e.stopPropagation()}>
                          {manageable && confirmDeleteId === doc.id ? (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleDelete(doc.id)}
                                disabled={deletingId !== null}
                                className="text-red-600 border-red-200 hover:bg-red-50 min-w-[44px] min-h-[44px]"
                              >
                                {deletingId === doc.id
                                  ? t('documents.list.delete.deleting')
                                  : t('documents.list.delete.confirmButton')}
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setConfirmDeleteId(null)}
                                disabled={deletingId !== null}
                                className="text-neutral-600 border-neutral-200 hover:bg-neutral-50 min-w-[44px] min-h-[44px]"
                              >
                                {t('documents.list.delete.cancel')}
                              </Button>
                            </>
                          ) : (
                            <>
                              {retryable && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleRederive(doc)}
                                  disabled={deletingId !== null || rederivingId === doc.id || bulkBusy}
                                  title={t('documents.list.retry.title', 'Retry processing')}
                                  className="text-primary border-primary/20 hover:bg-primary/5 min-w-[44px] min-h-[44px]"
                                >
                                  <RefreshCw className={`w-4 h-4${rederivingId === doc.id ? ' animate-spin' : ''}`} />
                                </Button>
                              )}
                              {manageable && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => {
                                    setSharingDocId(doc.id);
                                    setSharingDocTitle(doc.title || 'Untitled');
                                  }}
                                  disabled={deletingId !== null}
                                  className="text-primary border-primary/20 hover:bg-primary/5 min-w-[44px] min-h-[44px]"
                                  title={t('documents.list.share', 'Share document')}
                                >
                                  <Share2 className="w-4 h-4" />
                                </Button>
                              )}
                              {manageable && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setConfirmDeleteId(doc.id)}
                                  disabled={deletingId !== null || cannotDelete}
                                  title={cannotDelete ? t('documents.list.delete.cannotDeleteProcessing') : undefined}
                                  className="text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50 min-w-[44px] min-h-[44px]"
                                >
                                  {t('documents.list.delete.button')}
                                </Button>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Desktop: Table Layout */}
            <div className="hidden md:block bg-white border border-neutral-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10">
                        {manageableDocs.length > 0 && (
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-primary align-middle"
                            checked={allManageableSelected}
                            onChange={toggleSelectAll}
                            aria-label={t('documents.list.bulk.selectAll', 'Select all')}
                          />
                        )}
                      </TableHead>
                      <TableHead>{t('documents.list.table.title')}</TableHead>
                      <TableHead>{t('documents.list.table.category')}</TableHead>
                      <TableHead>{t('documents.list.table.status')}</TableHead>
                      <TableHead>{t('documents.list.table.visibility', 'Visibility')}</TableHead>
                      <TableHead>{t('documents.list.table.uploaded')}</TableHead>
                      <TableHead>{t('documents.list.table.chunks')}</TableHead>
                      <TableHead className="text-right">{t('documents.list.table.actions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {documents.map((doc) => {
                      const isProcessing = doc.status === 'processing';
                      const cannotDelete = isProcessing;
                      const manageable = canManage(doc);
                      const retryable = canRetry(doc) && isRetryable(doc);

                      return (
                        <TableRow
                          key={doc.id}
                          onClick={() => navigate(`/documents/${doc.id}`)}
                          className="cursor-pointer hover:bg-neutral-50"
                        >
                          <TableCell className="w-10" onClick={(e) => e.stopPropagation()}>
                            {manageable && (
                              <input
                                type="checkbox"
                                className="h-4 w-4 accent-primary align-middle"
                                checked={selectedIds.has(doc.id)}
                                onChange={() => toggleSelect(doc.id)}
                                aria-label={t('documents.list.bulk.selectRow', { title: doc.title || 'Untitled', defaultValue: 'Select {{title}}' })}
                              />
                            )}
                          </TableCell>
                          <TableCell className="font-medium max-w-xs">
                            <span className="block truncate">{doc.title || 'Untitled'}</span>
                            {doc.owner_id !== user?.id && doc.owner_name && (
                              <span className="block text-xs font-normal text-neutral-500 truncate">
                                {t('documents.list.byOwner', { name: doc.owner_name, defaultValue: 'by {{name}}' })}
                              </span>
                            )}
                          </TableCell>
                          <TableCell>{formatCategory(doc.category)}</TableCell>
                          <TableCell>
                            <StatusBadge
                              status={doc.status}
                              errorMessage={doc.error_message}
                              canAct={canRetry(doc)}
                            />
                          </TableCell>
                          <TableCell>
                            {renderVisibility(doc)}
                          </TableCell>
                          <TableCell className="text-neutral-500 text-sm">
                            {formatDayStamp(doc.created_at, t)}
                          </TableCell>
                          <TableCell className="text-neutral-500 text-sm">
                            {doc.chunk_count > 0 ? doc.chunk_count : '-'}
                          </TableCell>
                          <TableCell className="text-right">
                            {confirmDeleteId === doc.id ? (
                              <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                <span className="text-xs text-neutral-500 mr-2">
                                  {t('documents.list.delete.confirm')}
                                </span>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => handleDelete(doc.id)}
                                  disabled={deletingId !== null}
                                  className="text-red-600 border-red-200 hover:bg-red-50 min-w-[44px] min-h-[44px]"
                                >
                                  {deletingId === doc.id
                                    ? t('documents.list.delete.deleting')
                                    : t('documents.list.delete.confirmButton')}
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setConfirmDeleteId(null)}
                                  disabled={deletingId !== null}
                                  className="text-neutral-600 border-neutral-200 hover:bg-neutral-50 min-w-[44px] min-h-[44px]"
                                >
                                  {t('documents.list.delete.cancel')}
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-end gap-2" onClick={(e) => e.stopPropagation()}>
                                {retryable && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => handleRederive(doc)}
                                    disabled={deletingId !== null || rederivingId === doc.id || bulkBusy}
                                    title={t('documents.list.retry.title', 'Retry processing')}
                                    className="text-primary border-primary/20 hover:bg-primary/5 min-w-[44px] min-h-[44px]"
                                  >
                                    <RefreshCw className={`w-4 h-4${rederivingId === doc.id ? ' animate-spin' : ''}`} />
                                  </Button>
                                )}
                                {manageable && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => {
                                      setSharingDocId(doc.id);
                                      setSharingDocTitle(doc.title || 'Untitled');
                                    }}
                                    disabled={deletingId !== null}
                                    className="text-primary border-primary/20 hover:bg-primary/5 min-w-[44px] min-h-[44px]"
                                    title={t('documents.list.share', 'Share document')}
                                  >
                                    <Share2 className="w-4 h-4" />
                                  </Button>
                                )}
                                {manageable && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={() => setConfirmDeleteId(doc.id)}
                                    disabled={deletingId !== null || cannotDelete}
                                    title={cannotDelete ? t('documents.list.delete.cannotDeleteProcessing') : undefined}
                                    className="text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50 min-w-[44px] min-h-[44px]"
                                  >
                                    {t('documents.list.delete.button')}
                                  </Button>
                                )}
                              </div>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>

              {/* Load More - Desktop */}
              {documentsQuery.hasNextPage && (
                <div className="p-4 text-center border-t border-neutral-200">
                  <p className="text-xs text-neutral-400 mb-3">
                    {t('documents.list.pagination', { shown: documents.length, total })}
                  </p>
                  <Button
                    onClick={() => void documentsQuery.fetchNextPage()}
                    disabled={documentsQuery.isFetchingNextPage}
                    variant="outline"
                    size="sm"
                  >
                    {documentsQuery.isFetchingNextPage ? t('common.loading') : t('documents.list.loadMore')}
                  </Button>
                </div>
              )}
            </div>

            {/* Load More - Mobile */}
            {documentsQuery.hasNextPage && (
              <div className="md:hidden mt-4 p-4 text-center bg-white border border-neutral-200 rounded-lg">
                <p className="text-xs text-neutral-400 mb-3">
                  {t('documents.list.pagination', { shown: documents.length, total })}
                </p>
                <Button
                  onClick={() => void documentsQuery.fetchNextPage()}
                  disabled={documentsQuery.isFetchingNextPage}
                  variant="outline"
                  size="sm"
                >
                  {documentsQuery.isFetchingNextPage ? t('common.loading') : t('documents.list.loadMore')}
                </Button>
              </div>
            )}
          </>
        )}
      </main>
        </>
      )}

      {/* Share Dialog — also hosts the document's Visibility control; the
          live row flows back in through the list query after each change. */}
      {sharingDocId && (
        <ShareDialog
          resourceId={sharingDocId}
          resourceTitle={sharingDocTitle}
          resourceType="document"
          doc={documents.find((d) => d.id === sharingDocId)}
          open={sharingDocId !== null}
          onOpenChange={(open) => {
            if (!open) {
              setSharingDocId(null);
              setSharingDocTitle('');
            }
          }}
        />
      )}
    </div>
  );
}
